import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  AccessTime as ClockIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Assessment as ReportsIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { submitDemoRequest, getSignupEnabled } from '../api';

const features = [
  {
    icon: <ClockIcon sx={{ fontSize: 40 }} />,
    title: 'Time Tracking',
    description: 'Easy time entry with support for multiple pay rates, overtime tracking, and flexible date ranges.',
  },
  {
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    title: 'Employee Management',
    description: 'Manage employees, set pay rates, track affiliations, and control access with role-based permissions.',
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
    title: 'Multi-Company Support',
    description: 'Track time across multiple clients or companies with separate billing rates and reporting.',
  },
  {
    icon: <ReportsIcon sx={{ fontSize: 40 }} />,
    title: 'Comprehensive Reports',
    description: 'Generate detailed reports by employee, company, date range, and export to Excel for payroll.',
  },
  {
    icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
    title: 'Automated Emails',
    description: 'Schedule automatic timesheet reminder emails with customizable templates and attachments.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Secure & Multi-Tenant',
    description: 'Enterprise-grade security with isolated tenant data, role-based access, and audit logging.',
  },
];

function LandingPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    companySize: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Self-serve signup is invite-only unless enabled server-side. Hide the
  // "Get Started Free" CTA when it's off so we don't route people to a 403.
  const [signupEnabled, setSignupEnabled] = useState(false);

  useEffect(() => {
    getSignupEnabled()
      .then(res => setSignupEnabled(!!res.data.enabled))
      .catch(() => setSignupEnabled(false));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    try {
      await submitDemoRequest(formData);
      setSubmitSuccess(true);
      setFormData({
        companyName: '',
        contactName: '',
        email: '',
        phone: '',
        companySize: '',
        message: '',
      });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section — solid brand teal (design system: no gradients) */}
      <Box
        sx={{
          bgcolor: '#0B7C72',
          color: '#FFFFFF',
          py: 8,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ClockIcon sx={{ fontSize: 50, mr: 2 }} />
                <Typography variant="h3" fontWeight="bold">
                  HourTrax™
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                Professional Time Tracking & Payroll Management
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, opacity: 0.85, maxWidth: 500 }}>
                Streamline your workforce management with our comprehensive timesheet solution. 
                Track hours, manage employees, generate reports, and simplify payroll processing.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="#demo-request"
                  sx={{
                    bgcolor: '#FFFFFF',
                    color: '#0B7C72',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                  }}
                >
                  Request a Demo
                </Button>
                {signupEnabled && (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/signup')}
                    sx={{
                      bgcolor: '#FFFFFF',
                      color: '#0B7C72',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                    }}
                  >
                    Get Started Free
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="large"
                  href="#quick-access"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="text"
                  size="large"
                  onClick={() => navigate('/pricing')}
                  sx={{ color: 'white', opacity: 0.85, '&:hover': { opacity: 1 } }}
                >
                  View Pricing
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper
                id="quick-access"
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" gutterBottom color="primary">
                  Quick Access
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Already have an account? Enter your organization's URL to sign in.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {window.location.origin}/
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="your-company"
                    sx={{ flex: 1 }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        navigate(`/${e.target.value}`);
                      }
                    }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Press Enter to go to your organization's login page
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" textAlign="center" fontWeight="bold" gutterBottom>
          Everything You Need
        </Typography>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 6, maxWidth: 600, mx: 'auto' }}>
          A complete solution for time tracking, employee management, and payroll reporting.
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Demo Request Section */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: 1, borderBottom: 1, borderColor: 'divider', py: 8 }} id="demo-request">
        <Container maxWidth="md">
          <Typography variant="h4" textAlign="center" fontWeight="bold" gutterBottom>
            Request a Demo
          </Typography>
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mb: 4 }}>
            Interested in using HourTrax for your organization? Fill out the form below and we'll get in touch.
          </Typography>

          {submitSuccess ? (
            <Card sx={{ p: 4, textAlign: 'center', bgcolor: 'success.soft' }}>
              <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Thank You!
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your demo request has been submitted. We'll contact you shortly.
              </Typography>
              <Button
                variant="outlined"
                sx={{ mt: 3 }}
                onClick={() => setSubmitSuccess(false)}
              >
                Submit Another Request
              </Button>
            </Card>
          ) : (
            <Card sx={{ p: 4 }}>
              {submitError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {submitError}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Company Name"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Your Name"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Size"
                      name="companySize"
                      placeholder="e.g., 1-10, 11-50, 51-200"
                      value={formData.companySize}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Message / Requirements"
                      name="message"
                      placeholder="Tell us about your needs..."
                      value={formData.message}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={submitting}
                      startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                      {submitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          )}
        </Container>
      </Box>

      {/* Footer — fixed dark ground tone in both color modes */}
      <Box sx={{ bgcolor: '#0D1413', color: '#E9EEED', py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ClockIcon sx={{ mr: 1 }} />
                <Typography variant="h6">HourTrax™</Typography>
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                Professional Time Tracking & Payroll Management
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                © {new Date().getFullYear()} HourTrax™
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
